import { Box } from '@hover/blueprint';
import React from 'react';
import { Redirect } from 'react-router';

import { useAuthenticateWithCookie } from 'src/modules/propertyDetails/hooks/useAuthenticateWithCookie';
import { DelayedLoader } from 'src/shared/components';
import { useLocation } from 'src/shared/hooks';
import { useAuthExperience } from 'src/shared/hooks/useAuthExperience';
import { BrandProvider } from 'src/shared/providers/Brand';
import { isServer } from 'src/shared/utils/environment';
import { loginPathWithRedirectTo } from 'src/shared/utils/routing';

const AuthenticatedLayoutContent = ({
  children,
}: React.PropsWithChildren<{}>) => {
  const { loading, user } = useAuthExperience();
  const { pathname } = useLocation();

  if (!loading && !user) {
    // If we fail to authenticate on the server, then don't try
    // to SSR and return empty body. Let the client side run and
    // see if authentication still fails. If so, then redirect user.
    // The reason for this is that SSR uses a Hyperion cookie for auth
    // and client side uses JWT token. If Hyperion cookie is bad/missing/expired
    // then without this logic, the users ends up in an infinite redirect
    // because the client is authenticated, but their server session is not.
    return isServer() ? (
      <div />
    ) : (
      <Redirect to={loginPathWithRedirectTo(pathname)} />
    );
  }

  return (
    <BrandProvider>
      <Box
        flexDirection="column"
        maxWidth="100%"
        minHeight="100vh"
        width="100vw"
      >
        {loading ? (
          <Box
            alignItems="center"
            height="100svh"
            justifyContent="center"
            minW="100vw"
          >
            <DelayedLoader delay={1000} />
          </Box>
        ) : (
          children
        )}
      </Box>
    </BrandProvider>
  );
};

export const AuthenticatedLayout = ({
  children,
}: React.PropsWithChildren<{}>) => {
  const hasValidJwt = useAuthenticateWithCookie();

  // If they don't have a valid cookie, the hook will redirect them to login.
  // If they have a valid cookie, the client will update the user's JWTs and
  // render the page.
  if (!hasValidJwt) {
    return <div />;
  }

  return <AuthenticatedLayoutContent>{children}</AuthenticatedLayoutContent>;
};
